"use client";

import Link, { LinkProps } from "next/link";
import { useSearchParams } from "next/navigation";
import React, { useMemo } from "react";
import qs from "qs";

const pathnameRegex = /[^?]+/;

// we don't want these values to persist over page changes via a Link
export const queryParamsToPurge = [
  "filters",
  "pageIndex",
  "pageSize",
  "sortBy",
  "sortOrder",
  "tabValue",
  "openExportModal",
  "tablesToExport",
  "viewToExport",
  "influencerFilter",
  "placementTypeFilter",
  "creative_comparison_1",
  "creative_comparison_2",
  "creativesRowSelection",
  "selected_creative_chart",
  "newDashboard",
  "mapping_success",
  "token",
  "type",
  "recentActivityEventTypes",
  "recentActivityStart",
  "recentActivityEnd",
  "recentActivityOrdering",
];

export const dashboardDateQueryParams = [
  "startTime",
  "endTime",
  "timerange",
  "previousStartTime",
  "previousEndTime",
  "comparePeriod",
  "compare",
];

export const getQueryParamsToPurgeForPath = (path: string) => {
  const pathname = path.match(pathnameRegex)?.[0] ?? path;

  if (pathname === "/settings/recent-activity") {
    return [...queryParamsToPurge, ...dashboardDateQueryParams];
  }

  return queryParamsToPurge;
};

const LinkWithPersistentQuery = ({
  children,
  href,
  as,
  ...props
}: LinkProps & {
  children: React.ReactNode;
  href: string;
  as?: string;
  id?: string;
  className?: string;
}) => {
  const searchFromHref = useMemo(() => {
    const search = href.replace(pathnameRegex, "");
    return search;
  }, [href]);

  const searchFromAs = useMemo(() => {
    const search = as ? as.replace(pathnameRegex, "") : null;
    return search;
  }, [as]);
  const searchParams = useSearchParams();
  const currentSearch = searchParams?.toString() ?? "";

  const hrefWithoutSearch = useMemo(
    () =>
      searchFromHref ? href.replace(searchFromHref, "") : href ? href : "#",
    [href, searchFromHref]
  );
  const asWithoutSearch = useMemo(
    () => (as && searchFromAs ? as.replace(searchFromAs, "") : as ? as : "#"),
    [as, searchFromAs]
  );
  const totalSearch = useMemo(() => {
    // if we don't have a current search, return an empty string
    if (!searchFromHref && !currentSearch) {
      return { hrefSearch: "", asSearch: "" };
    }
    // get the query object from our current location without the ?
    const purgedRouterQuery = currentSearch ? qs.parse(currentSearch) : {};

    // remove query params we don't want to pass when changing pages
    getQueryParamsToPurgeForPath(hrefWithoutSearch).forEach(
      (el) => delete purgedRouterQuery[el]
    );
    // if we don't have a search added to the Link href, only return the
    // query we have created before as a search string
    if (!searchFromHref)
      return {
        hrefSearch:
          Object.keys(purgedRouterQuery).length > 0
            ? `?${qs.stringify(purgedRouterQuery)}`
            : "",
        asSearch: "",
      };

    // if we don't have a search string in our current location,
    // only return the search (if any) from the Link href
    if (!currentSearch)
      return {
        hrefSearch: searchFromHref,
        asSearch: searchFromAs ?? "",
      };

    // if we have a search in both the current location and the new Link href
    // we have to join these two searches to 1 single query object which then can
    // return as a unified search string
    const hrefQuery = qs.parse(searchFromHref.replace("?", ""));
    const asQuery = qs.parse(searchFromAs ?? searchFromHref);
    const completeHrefQuery = { ...purgedRouterQuery, ...hrefQuery };
    const completeAsQuery = { ...purgedRouterQuery, ...asQuery };
    const newHrefSearch =
      Object.keys(completeHrefQuery).length > 0
        ? `?${qs.stringify(completeHrefQuery)}`
        : "";
    const newAsSearch =
      Object.keys(completeAsQuery).length > 0
        ? `?${qs.stringify(completeAsQuery)}`
        : "";
    return { hrefSearch: newHrefSearch, asSearch: newAsSearch };
  }, [currentSearch, hrefWithoutSearch, searchFromAs, searchFromHref]);

  // if we have an as in the Link props, preferably return this
  if (as && asWithoutSearch) {
    return (
      <Link
        href={`${hrefWithoutSearch}${totalSearch.hrefSearch}`}
        as={`${asWithoutSearch}${totalSearch.asSearch}`}
        {...props}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link href={`${hrefWithoutSearch}${totalSearch.hrefSearch}`} {...props}>
      {children}
    </Link>
  );
};

export default LinkWithPersistentQuery;
